import { useMemo, useState, type ReactNode } from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown, Info } from "lucide-react";
import { cn } from "@/lib/utils";

export interface Column<T> {
  header: string;
  /** Hover explanation shown as an info icon beside the header text. */
  headerTooltip?: string;
  align?: "left" | "right";
  cell: (row: T) => ReactNode;
  /**
   * Makes the column sortable. Numeric columns sort descending on the first
   * click (biggest first), text ascending; null/undefined sorts last either
   * way. Omit for columns where ordering is meaningless (meters, actions).
   */
  sortValue?: (row: T) => string | number | null | undefined;
  /**
   * Raw value for CSV export (see CsvDownloadButton) when it should differ
   * from `sortValue` (e.g. sort by epoch, export the ISO string). Sortable
   * columns export their `sortValue` by default; columns with neither stay
   * screen-only.
   */
  csvValue?: (row: T) => string | number | null | undefined;
  /** CSV column header when the raw value doesn't match the on-screen one. */
  csvHeader?: string;
}

interface SortState {
  header: string;
  dir: "asc" | "desc";
}

/** Numeric columns open with the largest values, text columns with A→Z. */
function firstSortDir<T>(col: Column<T>, rows: T[]): SortState["dir"] {
  for (const row of rows) {
    const v = col.sortValue?.(row);
    if (v != null) return typeof v === "number" ? "desc" : "asc";
  }
  return "asc";
}

/** Native-title info icon; cheap and keyboard-discoverable via the header. */
function HeaderInfo({ text }: { text: string }) {
  return (
    <span title={text} className="cursor-help">
      <Info className="h-3.5 w-3.5 opacity-40" aria-label={text} role="img" />
    </span>
  );
}

// numeric:true keeps "Lane 10" after "Lane 2"; hoisted because a collator
// is expensive to build and the tables sort up to ~2,000 rows.
const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

/**
 * Plain scrollable table with a sticky header. Numeric columns should be
 * right-aligned; the body uses tabular figures so digits line up. Columns
 * with a `sortValue` get a click-to-sort header; the caller's row order is
 * the default until a header is clicked. Pair with CsvDownloadButton in the
 * card header for exports.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  maxHeight = "24rem",
  emptyMessage = "No rows",
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  maxHeight?: string;
  emptyMessage?: string;
}) {
  const [sort, setSort] = useState<SortState | null>(null);

  const sortCol = sort
    ? columns.find((c) => c.header === sort.header && c.sortValue)
    : undefined;

  const sortedRows = useMemo(() => {
    const value = sortCol?.sortValue;
    if (!sort || !value) return rows;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      if (av == null || bv == null) {
        // Missing values sort last regardless of direction.
        return (av == null ? 1 : 0) - (bv == null ? 1 : 0);
      }
      if (typeof av === "number" && typeof bv === "number") {
        return dir * (av - bv);
      }
      return dir * collator.compare(String(av), String(bv));
    });
  }, [rows, sort, sortCol]);

  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
  }

  const toggleSort = (col: Column<T>) => {
    setSort((prev) =>
      prev?.header === col.header
        ? { header: col.header, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { header: col.header, dir: firstSortDir(col, rows) },
    );
  };

  return (
    <div className="overflow-auto rounded-md border" style={{ maxHeight }}>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-muted-foreground">
            {columns.map((col) => {
              const active = sortCol?.header === col.header ? sort : null;
              return (
                // sticky lives on th (not thead) for Safari; the bottom rule
                // is an inset shadow because collapsed borders don't stick.
                <th
                  key={col.header}
                  aria-sort={
                    active
                      ? active.dir === "asc"
                        ? "ascending"
                        : "descending"
                      : undefined
                  }
                  className={cn(
                    "sticky top-0 bg-card px-3 py-2 font-medium shadow-[inset_0_-1px_0_hsl(var(--border))]",
                    col.align === "right" && "text-right",
                  )}
                >
                  {col.sortValue ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(col)}
                      className={cn(
                        "inline-flex items-center gap-1 font-medium hover:text-foreground",
                        active && "text-foreground",
                      )}
                    >
                      {col.header}
                      {col.headerTooltip ? (
                        <HeaderInfo text={col.headerTooltip} />
                      ) : null}
                      {active ? (
                        active.dir === "asc" ? (
                          <ArrowUp className="h-3.5 w-3.5" aria-hidden />
                        ) : (
                          <ArrowDown className="h-3.5 w-3.5" aria-hidden />
                        )
                      ) : (
                        <ChevronsUpDown
                          className="h-3.5 w-3.5 opacity-40"
                          aria-hidden
                        />
                      )}
                    </button>
                  ) : col.headerTooltip ? (
                    <span className="inline-flex items-center gap-1">
                      {col.header}
                      <HeaderInfo text={col.headerTooltip} />
                    </span>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="[font-variant-numeric:tabular-nums]">
          {sortedRows.map((row, i) => (
            <tr key={rowKey(row, i)} className="border-b last:border-0">
              {columns.map((col) => (
                <td
                  key={col.header}
                  className={cn(
                    "px-3 py-2",
                    col.align === "right" && "text-right",
                  )}
                >
                  {col.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
